// src/components/dashboard/reports/ClientsReport.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  TablePagination,
  Grid,
} from "@mui/material";

// Core Guaranteed MUI Icons
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const ClientsReport = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPractice, setSelectedPractice] = useState("All Practices");
  const [selectedStatus, setSelectedStatus] = useState("All Statuses");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Standardized Healthcare Client/Patient Registry Report
  const [clients] = useState([
    {
      id: "CLI-9012",
      name: "Eleanor Vance",
      dob: "04/12/1984",
      age: 42,
      gender: "Female",
      primaryInsurance: "Medicaid State Plan",
      medicaidId: "MN-982104",
      assignedProvider: "Dr. Sarah Jenkins",
      practice: "Main Clinic",
      lastVisit: "09/10/2026",
      authStatus: "Active",
      authExpiry: "12/31/2026",
    },
    {
      id: "CLI-9013",
      name: "Marcus Aurelius Vance",
      dob: "11/23/1976",
      age: 49,
      gender: "Male",
      primaryInsurance: "Blue Cross Blue Shield",
      medicaidId: "BC-449102",
      assignedProvider: "Dr. Usman Rehman",
      practice: "Main Clinic",
      lastVisit: "09/12/2026",
      authStatus: "Active",
      authExpiry: "11/15/2026",
    },
    {
      id: "CLI-9014",
      name: "Sophia Martinez",
      dob: "08/19/1992",
      age: 34,
      gender: "Female",
      primaryInsurance: "UnitedHealthcare Community",
      medicaidId: "UHC-77182",
      assignedProvider: "Dr. Bilal",
      practice: "Outpatient Center",
      lastVisit: "08/29/2026",
      authStatus: "Expiring Soon",
      authExpiry: "09/30/2026",
    },
    {
      id: "CLI-9015",
      name: "James Sterling",
      dob: "02/05/1961",
      age: 65,
      gender: "Male",
      primaryInsurance: "Medicare Part B",
      medicaidId: "MED-55019",
      assignedProvider: "Dr. Usman Rehman",
      practice: "Specialty Neuro Rehab",
      lastVisit: "09/14/2026",
      authStatus: "Active",
      authExpiry: "03/10/2027",
    },
    {
      id: "CLI-9016",
      name: "Amina Al-Mansoor",
      dob: "06/30/1988",
      age: 38,
      gender: "Female",
      primaryInsurance: "Aetna Health",
      medicaidId: "AET-33019",
      assignedProvider: "Dr. Sarah Jenkins",
      practice: "Telehealth Care",
      lastVisit: "07/15/2026",
      authStatus: "On-Hold",
      authExpiry: "08/01/2026",
    },
    {
      id: "CLI-9017",
      name: "David Chen",
      dob: "09/14/1979",
      age: 47,
      gender: "Male",
      primaryInsurance: "Medicaid State Plan",
      medicaidId: "MN-109284",
      assignedProvider: "Dr. Bilal",
      practice: "Main Clinic",
      lastVisit: "09/01/2026",
      authStatus: "Active",
      authExpiry: "01/18/2027",
    },
  ]);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  // Filter Logic
  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.medicaidId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesPractice =
      selectedPractice === "All Practices" || client.practice === selectedPractice;

    const matchesStatus =
      selectedStatus === "All Statuses" || client.authStatus === selectedStatus;

    return matchesSearch && matchesPractice && matchesStatus;
  });

  const getStatusChip = (status) => {
    switch (status) {
      case "Active":
        return {
          bg: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
          color: isDark ? "#34D399" : "#15803D",
        };
      case "Expiring Soon":
        return {
          bg: isDark ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
          color: isDark ? "#FBBF24" : "#B45309",
        };
      case "On-Hold":
        return {
          bg: isDark ? "rgba(239, 68, 68, 0.15)" : "#FEE2E2",
          color: isDark ? "#F87171" : "#B91C1C",
        };
      default:
        return {
          bg: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
          color: secondaryTextColor,
        };
    }
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", pb: 6, boxSizing: "border-box" }}>
      {/* 1. TOP HEADER BANNER & ACTIONS */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AssessmentRoundedIcon sx={{ fontSize: "1.75rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Clients Report
              </Typography>
              <Chip
                label="Census & Authorizations"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Comprehensive active client census, authorization timelines, and demographic breakdown
            </Typography>
          </Box>
        </Box>

        {/* Export Action Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, alignSelf: { xs: "stretch", sm: "auto" } }}>
          <Button
            variant="outlined"
            startIcon={<PrintRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            onClick={() => window.print()}
            sx={{
              height: "40px",
              borderRadius: "6px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              px: 2,
              "&:hover": {
                borderColor: tealMain,
                backgroundColor: "rgba(36, 94, 101, 0.04)",
              },
            }}
          >
            Print
          </Button>

          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: addGreen,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.5,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* 2. EXECUTIVE KPI SUMMARY METRIC CARDS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            title: "Total Registered Clients",
            value: "148",
            icon: <PeopleAltRoundedIcon sx={{ fontSize: "1.3rem" }} />,
            sub: "+8 this month",
          },
          {
            title: "Active Primary Caseload",
            value: "132",
            icon: <VerifiedUserRoundedIcon sx={{ fontSize: "1.3rem" }} />,
            sub: "89.1% coverage",
          },
          {
            title: "Expiring Authorizations",
            value: "9",
            icon: <WarningAmberRoundedIcon sx={{ fontSize: "1.3rem" }} />,
            sub: "Action required",
          },
          {
            title: "Pending Onboarding",
            value: "7",
            icon: <PersonAddAltRoundedIcon sx={{ fontSize: "1.3rem" }} />,
            sub: "Intake queue",
          },
        ].map((kpi, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Box
              sx={{
                backgroundColor: cardBg,
                borderRadius: "14px",
                border: `1px solid ${softCardBorder}`,
                p: 2,
                boxShadow: isDark
                  ? "0 4px 20px rgba(0,0,0,0.3)"
                  : "0 2px 10px rgba(15, 23, 42, 0.02)",
                display: "flex",
                alignItems: "center",
                gap: 1.8,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {kpi.icon}
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: primaryTextColor, lineHeight: 1.1 }}>
                    {kpi.value}
                  </Typography>
                  <Chip
                    label={kpi.sub}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      height: "18px",
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
                      color: secondaryTextColor,
                      borderRadius: "4px",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor, fontWeight: 700, textTransform: "uppercase", mt: 0.3 }}>
                  {kpi.title}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* 3. MAIN REPORT TABLE CONTAINER + FILTERS */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark
            ? "0 4px 20px rgba(0, 0, 0, 0.3)"
            : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        {/* Dynamic Filter Strip */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            p: 2.2,
            borderBottom: `1px solid ${softCardBorder}`,
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.01)" : "#FDFDFD",
          }}
        >
          {/* Search Box */}
          <TextField
            size="small"
            placeholder="Search by client name, ID, or Medicaid #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: secondaryTextColor, fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: "360px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
              },
            }}
          />

          {/* Filter Selectors */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {/* Practice Filter */}
            <Select
              size="small"
              value={selectedPractice}
              onChange={(e) => setSelectedPractice(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              sx={{
                minWidth: "160px",
                height: "40px",
                borderRadius: "6px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="All Practices">All Practices</MenuItem>
              <MenuItem value="Main Clinic">Main Clinic</MenuItem>
              <MenuItem value="Outpatient Center">Outpatient Center</MenuItem>
              <MenuItem value="Specialty Neuro Rehab">Specialty Neuro Rehab</MenuItem>
              <MenuItem value="Telehealth Care">Telehealth Care</MenuItem>
            </Select>

            {/* Status Filter */}
            <Select
              size="small"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              sx={{
                minWidth: "150px",
                height: "40px",
                borderRadius: "6px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="All Statuses">All Statuses</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Expiring Soon">Expiring Soon</MenuItem>
              <MenuItem value="On-Hold">On-Hold</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Data Grid Table */}
        <TableContainer
          sx={{
            maxHeight: "600px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1050 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "110px" }}>Client ID</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>DOB / Age</TableCell>
                <TableCell>Primary Insurance</TableCell>
                <TableCell>Assigned Clinician</TableCell>
                <TableCell>Practice Location</TableCell>
                <TableCell>Last Encounter</TableCell>
                <TableCell>Auth Status</TableCell>
                <TableCell align="right" sx={{ width: "80px", pr: 3 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No client records match the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const statusChip = getStatusChip(row.authStatus);

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          transition: "background-color 0.15s ease",
                          "&:hover": { backgroundColor: rowHoverBg },
                          "& td": {
                            borderBottom: `1px solid ${
                              isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                            }`,
                            py: 1.4,
                          },
                        }}
                      >
                        {/* Client ID */}
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: "6px",
                              backgroundColor: pastelTealBg,
                              color: tealButton,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                            }}
                          >
                            {row.id}
                          </Box>
                        </TableCell>

                        {/* Name & Gender */}
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.86rem" }}>
                            {row.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.74rem", color: secondaryTextColor }}>
                            {row.gender}
                          </Typography>
                        </TableCell>

                        {/* DOB & Age */}
                        <TableCell>
                          <Typography sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                            {row.dob}
                          </Typography>
                          <Typography sx={{ fontSize: "0.74rem", color: secondaryTextColor }}>
                            {row.age} yrs
                          </Typography>
                        </TableCell>

                        {/* Primary Insurance & ID */}
                        <TableCell>
                          <Typography sx={{ color: primaryTextColor, fontSize: "0.84rem", fontWeight: 600 }}>
                            {row.primaryInsurance}
                          </Typography>
                          <Typography sx={{ fontSize: "0.76rem", color: secondaryTextColor, fontFamily: "monospace" }}>
                            {row.medicaidId}
                          </Typography>
                        </TableCell>

                        {/* Assigned Clinician */}
                        <TableCell sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                          {row.assignedProvider}
                        </TableCell>

                        {/* Practice */}
                        <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem" }}>
                          {row.practice}
                        </TableCell>

                        {/* Last Encounter */}
                        <TableCell sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                          {row.lastVisit}
                        </TableCell>

                        {/* Auth Status */}
                        <TableCell>
                          <Chip
                            label={row.authStatus}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              backgroundColor: statusChip.bg,
                              color: statusChip.color,
                            }}
                          />
                        </TableCell>

                        {/* Action View */}
                        <TableCell align="right" sx={{ pr: 2.5 }}>
                          <Tooltip title="View Client File">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                p: 0.6,
                                borderRadius: "6px",
                                "&:hover": {
                                  color: tealMain,
                                  backgroundColor: pastelTealBg,
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredClients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ClientsReport;